import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useApp } from '../context/AppContext.jsx';
import FoxMark from '../components/shared/FoxMark.jsx';
import { authenticatedFetch } from '../services/apiClient.js';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const GOLD = '#A67C3A';

export default function Subscribe() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { guide, userProfile } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubscribe() {
    setLoading(true);
    setError('');

    try {
      const response = await authenticatedFetch('/api/create-checkout', {
        method: 'POST',
        body: JSON.stringify({
          email: user?.primaryEmailAddress?.emailAddress || '',
          name: userProfile?.name || user?.firstName || '',
        }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[Subscribe] Error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAPER,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <FoxMark variant="gold" size={52} />
      </div>

      <h1
        style={{
          fontFamily: 'Georgia, serif',
          fontSize: 30,
          fontWeight: 300,
          color: INK,
          textAlign: 'center',
          marginBottom: 8,
          lineHeight: 1.3,
        }}
      >
        Continue with {guide?.name || 'your guide'}
      </h1>

      <p
        style={{
          fontSize: 15,
          color: INK,
          opacity: 0.6,
          textAlign: 'center',
          lineHeight: 1.7,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          marginBottom: 32,
          maxWidth: 340,
        }}
      >
        Your five-day trial is complete. To keep going — and to see how
        your study deepens over time — continue your journey.
      </p>

      <div
        style={{
          background: 'white',
          border: `1.5px solid rgba(166,124,58,0.3)`,
          borderRadius: 16,
          padding: '24px 28px',
          width: '100%',
          maxWidth: 340,
          textAlign: 'center',
          marginBottom: 20,
        }}
      >
        <p
          style={{
            fontSize: 12,
            color: GOLD,
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Rooted
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: 4,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 42,
              fontWeight: 300,
              color: INK,
            }}
          >
            $5.99
          </span>
          <span style={{ fontSize: 14, color: INK, opacity: 0.5 }}>
            /week
          </span>
        </div>
        <p
          style={{
            fontSize: 13,
            color: INK,
            opacity: 0.4,
            marginBottom: 20,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
          }}
        >
          Cancel anytime
        </p>

        <div style={{ textAlign: 'left', marginBottom: 20 }}>
          {[
            'Personalized daily Bible study',
            'Your Enneagram profile built in',
            'Voice guide — Jess or Archer',
            'Daily check-ins that adapt your study',
            'Journal and highlights saved',
          ].map((item, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                marginBottom: 8,
              }}
            >
              <span style={{ color: GOLD, fontSize: 16, lineHeight: 1.4 }}>
                ✓
              </span>
              <span
                style={{
                  fontSize: 14,
                  color: INK,
                  opacity: 0.7,
                  fontFamily: 'Georgia, serif',
                  lineHeight: 1.4,
                }}
              >
                {item}
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={handleSubscribe}
          disabled={loading}
          style={{
            background: INK,
            color: PAPER,
            border: 'none',
            borderRadius: 12,
            padding: '16px',
            fontSize: 16,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontFamily: 'Georgia, serif',
            width: '100%',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Loading…' : 'Start my subscription'}
        </button>

        {error && (
          <p
            style={{
              fontSize: 13,
              color: '#c93535',
              marginTop: 12,
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}
      </div>

      <p
        style={{
          fontSize: 12,
          color: INK,
          opacity: 0.4,
          textAlign: 'center',
          lineHeight: 1.6,
          maxWidth: 300,
        }}
      >
        You'll be charged $5.99 per week starting today. Cancel anytime
        from your account settings.
      </p>

      <button
        type="button"
        onClick={() => navigate('/study')}
        style={{
          background: 'none',
          border: 'none',
          color: INK,
          opacity: 0.4,
          fontSize: 13,
          cursor: 'pointer',
          marginTop: 16,
        }}
      >
        Back to my study
      </button>

      <p
        style={{
          marginTop: 32,
          fontSize: 10,
          fontFamily: 'Arial, sans-serif',
          color: '#A67C3A',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          opacity: 0.55,
          textAlign: 'center',
        }}
      >
        Powered by Golden Fox Publishing
      </p>
    </div>
  );
}
